import React from 'react';
// Implement our own numeric value checker instead of importing from backend
function isNumeric(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  if (typeof value === 'number') {
    return true;
  }

  if (typeof value === 'string') {
    // Check for currency values or numeric strings
    if (
      value.includes('$') ||
      value.includes('€') ||
      value.includes('£') ||
      value.endsWith('%') ||
      /^-?\d+(,\d+)*(\.\d+)?$/.test(value) ||
      /^-?\d+(\.\d+)*(,\d+)?$/.test(value)
    ) {
      return true;
    }
  }

  return false;
}

// Function to handle BOM characters in CSV data
function removeBOM(str: string): string {
  if (str.charCodeAt(0) === 0xFEFF) {
    return str.slice(1);
  }
  return str;
}

interface CSVPreviewProps {
  headers: string[];
  data: Record<string, unknown>[];
  maxRows?: number;
  validationSample?: {
    hasIssues: boolean;
    rowsWithIssues?: number[];
    issueCount?: number;
  };
  fileMetadata?: {
    size?: number;
    encoding?: string;
    hasBOM?: boolean;
    detectedDelimiter?: string;
  };
}

const CSVPreviewComponent: React.FC<CSVPreviewProps> = ({
  headers,
  data,
  maxRows = 5,
  validationSample,
  fileMetadata
}) => {
  // Don't render if we don't have data or headers
  if (!headers?.length || !data?.length) {
    return null;
  }

  // Process headers to remove any BOM characters
  const cleanedHeaders = headers.map(header => removeBOM(header));

  // Only display up to maxRows
  const displayData = data.slice(0, maxRows);

  // Function to detect data types for better visualization
  const detectDataType = (value: unknown): 'currency' | 'percentage' | 'number' | 'text' | 'empty' => {
    if (value === null || value === undefined || value === '') {
      return 'empty';
    }

    if (typeof value !== 'string') {
      return typeof value === 'number' ? 'number' : 'text';
    }

    const stringValue = value.toString().trim();

    // Check for currency
    if (stringValue.includes('$') || stringValue.includes('€') || stringValue.includes('£')) {
      return 'currency';
    }

    // Check for percentage
    if (stringValue.endsWith('%')) {
      return 'percentage';
    }

    // Check if it's a numeric value
    if (/^-?\d+(,\d+)*(\.\d+)?$/.test(stringValue) || /^-?\d+(\.\d+)*(,\d+)?$/.test(stringValue)) {
      return 'number';
    }

    return 'text';
  };

  // Determine column data types based on first few rows
  const columnTypes = headers.map(header => {
    // Look at values in the first few rows for this header
    const values = displayData.map(row => row[header]).filter(value => value !== null && value !== undefined && value !== '');
    
    // Count occurrences of each type
    const typeCounts = {
      currency: 0,
      percentage: 0,
      number: 0,
      text: 0,
      empty: 0
    };
    
    values.forEach(value => {
      const type = detectDataType(value);
      typeCounts[type]++;
    });
    
    // Determine the predominant type
    const totalValues = values.length;
    if (totalValues === 0) return 'text';
    
    if (typeCounts.currency / totalValues >= 0.5) return 'currency';
    if (typeCounts.percentage / totalValues >= 0.5) return 'percentage';
    if (typeCounts.number / totalValues >= 0.5) return 'number';
    return 'text';
  });

  return (
    <div className="overflow-x-auto rounded border">
      <div className="flex justify-between items-center text-sm p-2 bg-gray-100 font-medium">
        <span>CSV Preview (showing {displayData.length} of {data.length} rows)</span>
        {validationSample && (
          <span className={`${validationSample.hasIssues ? 'text-yellow-600' : 'text-green-600'} text-xs`}>
            {validationSample.hasIssues
              ? `${validationSample.issueCount || 'Some'} issues found in sample`
              : 'Sample validation passed'}
          </span>
        )}
      </div>

      {/* File metadata information */}
      {fileMetadata && (
        <div className="flex flex-wrap gap-2 text-xs p-2 bg-gray-50 border-b">
          {fileMetadata.size !== undefined && (
            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md">
              Size: {(fileMetadata.size / 1024).toFixed(1)} KB
            </span>
          )}
          {fileMetadata.encoding && (
            <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-md">
              Encoding: {fileMetadata.encoding}
            </span>
          )}
          {fileMetadata.hasBOM !== undefined && (
            <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md">
              BOM: {fileMetadata.hasBOM ? 'Yes' : 'No'}
            </span>
          )}
          {fileMetadata.detectedDelimiter && (
            <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded-md">
              Delimiter: {fileMetadata.detectedDelimiter === ',' ? 'Comma' :
                          fileMetadata.detectedDelimiter === ';' ? 'Semicolon' :
                          fileMetadata.detectedDelimiter === '\t' ? 'Tab' :
                          fileMetadata.detectedDelimiter}
            </span>
          )}
        </div>
      )}

      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider w-10">#</th>
            {cleanedHeaders.map((header, index) => (
              <th key={index} className="px-3 py-2 text-left">
                <div className="font-medium text-gray-900">{header}</div>
                <div className="text-xs text-gray-500 mt-1">
                  <span className={`inline-block px-2 py-1 rounded ${
                    columnTypes[index] === 'currency' ? 'bg-green-100 text-green-800' :
                    columnTypes[index] === 'percentage' ? 'bg-blue-100 text-blue-800' :
                    columnTypes[index] === 'number' ? 'bg-purple-100 text-purple-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {columnTypes[index]}
                  </span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {displayData.map((row, rowIndex) => {
            // Check if this row has validation issues
            const hasIssues = validationSample?.rowsWithIssues?.includes(rowIndex + 1);

            return (
              <tr
                key={rowIndex}
                className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${hasIssues ? 'bg-yellow-50' : ''}`}
              >
                <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                  {rowIndex + 1}
                  {hasIssues && (
                    <span className="ml-1 inline-block w-2 h-2 rounded-full bg-yellow-400" title="This row has validation issues"></span>
                  )}
                </td>
                {cleanedHeaders.map((header, colIndex) => {
                  const value = row[header];
                  const type = detectDataType(value);

                  // Apply styling based on data type
                  let cellClass = 'px-3 py-2 whitespace-nowrap ';
                  switch (type) {
                    case 'currency':
                      cellClass += 'text-green-700 font-medium';
                      break;
                    case 'percentage':
                      cellClass += 'text-blue-700 font-medium';
                      break;
                    case 'number':
                      cellClass += 'text-purple-700 font-medium';
                      break;
                    case 'empty':
                      cellClass += 'text-gray-300 italic';
                      break;
                    default:
                      cellClass += 'text-gray-900';
                  }

                  return (
                    <td key={colIndex} className={cellClass}>
                      {value === '' || value === null || value === undefined ?
                        <span className="text-gray-300 italic">empty</span> :
                        <>{typeof value === 'string' ? removeBOM(value) : value}</>}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CSVPreviewComponent;